import { api } from "@/services";
import { API_ENDPOINTS } from "@/config";

export interface RedemptionQuoteData {
    isDirectlyRedeemable: boolean;
    amountReceiptEnqueued: string;
    amountReceiptEnqueuedNormalized: number;
    amountAssetDirectlyRedeemable: string;
    amountAssetDirectlyRedeemableNormalized: number;
    amountAssetMissing: string;
    amountAssetMissingNormalized: number;
    fullyFundedTimestampMs: number;
    fullyFundedTimestampUTC: string;
}

export interface RedemptionQuoteResponse {
    code: string;
    data: RedemptionQuoteData;
}

export interface RedemptionPosition {
    amount: string; // iUSD amount
    amountNormalized: number;
    status: "queued" | "funded" | "claimed";
    queuePosition: number; // Position in redemption queue
    estimatedWaitTime: number; // Estimated wait time in milliseconds
    queuedTimestamp: number; // When position was queued
    queuedTimestampMs: number;
    fundedTimestamp?: number; // When position was funded (if applicable)
    fundedTimestampMs?: number;
    claimableAmount?: string; // USDC amount available to claim
    claimableAmountNormalized?: number;
    totalQueueSize: number; // Total number of positions in queue
    totalQueueAmount: string; // Total amount in queue
    totalQueueAmountNormalized: number;
}

export interface RedemptionQueueInfo {
    totalPositions: number;
    totalAmount: string;
    totalAmountNormalized: number;
    averageWaitTime: number; // Average wait time in milliseconds
    liquidityAvailable: string; // Available liquidity for immediate redemptions
    liquidityAvailableNormalized: number;
    fullyFundedTimestampMs: number;
}

export interface UserRedemptionsData {
    positions: RedemptionPosition[];
    queueInfo: RedemptionQueueInfo;
    totalQueued: string;
    totalQueuedNormalized: number;
    totalFunded: string;
    totalFundedNormalized: number;
    totalClaimed: string;
    totalClaimedNormalized: number;
}

export interface RedemptionTicket {
    user: string;
    amountReceipt: string;
    amountReceiptNormalized: number;
    enqueuedTimestampMs: number;
    enqueuedTimestampUTC: string;
    fullyFundedTimestampMs: number;
    fullyFundedTimestampUTC: string;
}

export interface RedemptionQueueData {
    lastUpdateTimestampMs: number;
    lastUpdateTimestampUTC: string;
    totalEnqueuedReceipt: string;
    totalEnqueuedReceiptNormalized: number;
    fullyFundedTimestampMs: number;
    fullyFundedTimestampUTC: string;
    tickets: RedemptionTicket[];
}

export interface RedemptionQueueResponse {
    code: string;
    data: RedemptionQueueData;
}
export interface UserRedemptionTicket {
    user: string;
    amountReceipt: string;
    amountReceiptNormalized: number;
    enqueuedTimestampMs: number;
    enqueuedTimestampUTC: string;
    fullyFundedTimestampMs: number;
    fullyFundedTimestampUTC: string;
}

export interface UserRedemptionDataResponse {
    claimableAssets: string;
    claimableAssetNormalized: number;
    totalEnqueuedReceipt: string;
    totalEnqueuedReceiptNormalized: number;
    fullyFundedTimestampMs: number;
    fullyFundedTimestampUTC: string;
    redemptionTickets: UserRedemptionTicket[];
}

export interface UserRedemptionResponse {
    code: string;
    data: UserRedemptionDataResponse;
}

export const fetchSimulatedRedemption = async (amount: number): Promise<RedemptionQuoteResponse> => {
    try {
        const response = await api.get<RedemptionQuoteResponse>(
            API_ENDPOINTS.PROTOCOL.REDEMPTIONS.ESTIMATE_REDEMPTION(amount),
        );
        const data = response.data;

        return data;
    } catch (error) {
        console.error("Error fetching user redemptions:", error);
        throw error;
    }
};

export const fetchUserRedemptions = async (address: string): Promise<UserRedemptionsData> => {
    try {
        const [globalRes, userRes] = await Promise.all([
            api.get<RedemptionQueueResponse>(API_ENDPOINTS.PROTOCOL.REDEMPTIONS.GET_GLOBAL_REDEMPTIONS),
            api.get<UserRedemptionResponse>(API_ENDPOINTS.PROTOCOL.REDEMPTIONS.GET_USER_REDEMPTIONS(address)),
        ]);

        const globalData = globalRes.data.data;
        const userData = userRes.data.data;
        const now = Date.now();

        const totalPositions = globalData.tickets.length;
        const totalAmount = globalData.totalEnqueuedReceipt;
        const totalAmountNormalized = globalData.totalEnqueuedReceiptNormalized;

        const positions: RedemptionPosition[] = userData.redemptionTickets.map((ticket) => {
            const queuePosition = globalData.tickets.findIndex(
                (t) =>
                    t.user.toLowerCase() === ticket.user.toLowerCase() &&
                    t.amountReceipt === ticket.amountReceipt &&
                    t.enqueuedTimestampMs === ticket.enqueuedTimestampMs,
            );

            return {
                amount: ticket.amountReceipt,
                amountNormalized: ticket.amountReceiptNormalized,
                status: "queued",
                queuePosition: queuePosition >= 0 ? queuePosition : -1,
                estimatedWaitTime: Math.max(ticket.fullyFundedTimestampMs - now, 0),
                queuedTimestamp: ticket.enqueuedTimestampMs,
                queuedTimestampMs: ticket.enqueuedTimestampMs,
                totalQueueSize: totalPositions,
                totalQueueAmount: totalAmount,
                totalQueueAmountNormalized: totalAmountNormalized,
            };
        });

        // Manually include funded position if it exists
        if (userData.claimableAssetNormalized > 0) {
            positions.push({
                amount: userData.claimableAssets,
                amountNormalized: userData.claimableAssetNormalized,
                status: "funded",
                queuePosition: -1,
                estimatedWaitTime: 0,
                queuedTimestamp: 0,
                queuedTimestampMs: 0,
                fundedTimestamp: userData.fullyFundedTimestampMs,
                fundedTimestampMs: userData.fullyFundedTimestampMs,
                claimableAmount: userData.claimableAssets,
                claimableAmountNormalized: userData.claimableAssetNormalized,
                totalQueueSize: totalPositions,
                totalQueueAmount: totalAmount,
                totalQueueAmountNormalized: totalAmountNormalized,
            });
        }

        return {
            positions,
            totalQueued: userData.totalEnqueuedReceipt,
            totalQueuedNormalized: userData.totalEnqueuedReceiptNormalized,
            totalFunded: "0",
            totalFundedNormalized: 0,
            totalClaimed: "0",
            totalClaimedNormalized: 0,
            queueInfo: {
                totalPositions,
                totalAmount,
                totalAmountNormalized,
                averageWaitTime: -1,
                liquidityAvailable: "0", //TODO FIX THIS
                liquidityAvailableNormalized: 0, //TODO FIX THIS
                fullyFundedTimestampMs: globalData.fullyFundedTimestampMs,
            },
        };
    } catch (error) {
        console.error("Error fetching user redemptions:", error);
        throw error;
    }
};
